<script>
  import { scanState, addScanEvent, setScanStatus, setScanProgress, setLastScanAt } from '../stores/scan.js';
  import { appStatus, pushNotice } from '../stores/ui.js';
  import { licenseState } from '../stores/license.js';
  import { authState } from '../stores/auth.js';
  import { getVaultKey } from '../stores/vault.js';
  import { runScan } from '../lib/youtube/scan.js';
  import { getApiKey, getDefaultChannelId } from '../lib/youtube/config.js';
  import { getTokenStatus } from '../lib/youtube/session.js';
  import { refreshLocalCounts } from '../stores/localdb.js';
  import { onMount } from 'svelte';

  const stages = [
    'OAuth token verified',
    'Channels list fetched',
    'Videos diffing (1-day buffer)',
    'IndexedDB sync',
    'Quota log stored'
  ];

  onMount(() => {
    appStatus.set({
      primary: 'Scan center',
      secondary: 'Runs only when online + authenticated'
    });
  });

  async function handleScan() {
    try {
      setScanStatus('running');
      addScanEvent('Scan started.');
      const vaultKey = getVaultKey();
      const tokenStatus = await getTokenStatus(vaultKey);
      const apiKey = await getApiKey();
      const channelId = await getDefaultChannelId();
      if (tokenStatus.status !== 'available' && (!apiKey || !channelId)) {
        setScanStatus('idle');
        pushNotice('Connect YouTube or add API key + channel ID first.', 'warning');
        return;
      }
      await runScan({
        vaultKey,
        onProgress: ({ queuedChannels, processedChannels }) =>
          setScanProgress({ queuedChannels, processedChannels }),
        onEvent: (message) => addScanEvent(message)
      });
      setLastScanAt(new Date().toISOString());
      setScanStatus('idle');
      await refreshLocalCounts();
      pushNotice('Scan complete.', 'success');
    } catch (error) {
      setScanStatus('error');
      addScanEvent(error.message || 'Scan failed.');
      pushNotice(error.message || 'Scan failed.', 'error');
    }
  }
</script>

{#if !$authState.isAuthenticated || !$licenseState.active}
  <section class="panel">
    <div class="panel-title">Paywall</div>
    <p>You need an active plan to scan. Please log in and activate your license.</p>
  </section>
{:else}
<section class="header">
  <div>
    <p class="eyebrow">Scan</p>
    <h1>Live scan control center.</h1>
    <p class="lead">Trigger scans, track quota usage, and resume interrupted runs.</p>
  </div>
  <button class="primary" on:click={handleScan}>Start scan</button>
</section>

<section class="grid">
  <div class="panel">
    <div class="panel-title">Scan progress</div>
    <div class="progress">
      <div class="progress-bar" style="width: 45%"></div>
    </div>
    <div class="panel-row">
      <span>Status</span>
      <strong>{ $scanState.status }</strong>
    </div>
    <div class="panel-row">
      <span>Queued channels</span>
      <strong>{ $scanState.queuedChannels }</strong>
    </div>
    <div class="panel-row">
      <span>Processed</span>
      <strong>{ $scanState.processedChannels }</strong>
    </div>
  </div>

  <div class="panel">
    <div class="panel-title">Quota & resume</div>
    <div class="panel-row">
      <span>Quota remaining</span>
      <strong>{ $scanState.quotaRemaining }</strong>
    </div>
    <div class="panel-row">
      <span>Resume token</span>
      <strong>Stored locally</strong>
    </div>
    <button class="secondary">View quota log</button>
  </div>

  <div class="panel wide">
    <div class="panel-title">Pipeline stages</div>
    <div class="timeline">
      {#each stages as stage, index}
        <div class="stage">
          <div class="stage-index">{index + 1}</div>
          <div>
            <div class="stage-title">{stage}</div>
            <div class="stage-sub">Handled in-browser, encrypted at rest.</div>
          </div>
        </div>
      {/each}
    </div>
  </div>
</section>

<section class="panel">
  <div class="panel-title">Recent events</div>
  {#if $scanState.recentEvents.length === 0}
    <div class="empty">No scan events yet.</div>
  {:else}
    {#each $scanState.recentEvents as event}
      <div class="event">
        <div>{event.message}</div>
        <div class="event-time">{event.createdAt}</div>
      </div>
    {/each}
  {/if}
</section>
{/if}

<style>
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  .eyebrow {
    font-size: 0.75rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--muted);
  }

  h1 {
    margin: 0.5rem 0 0.75rem;
    font-size: clamp(2rem, 3vw, 2.6rem);
  }

  .lead {
    color: var(--muted);
  }

  .primary,
  .secondary {
    padding: 0.65rem 1.2rem;
    border-radius: 999px;
    border: none;
    cursor: pointer;
    font-weight: 600;
  }

  .primary {
    background: var(--accent);
    color: #1b1b1b;
  }

  .secondary {
    background: rgba(31, 41, 51, 0.1);
  }

  .grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    margin-bottom: 1.5rem;
  }

  .panel {
    background: #fff;
    border-radius: 18px;
    padding: 1.5rem;
    border: 1px solid rgba(31, 41, 51, 0.08);
    display: grid;
    gap: 0.75rem;
  }

  .panel.wide {
    grid-column: 1 / -1;
  }

  .panel-title {
    font-weight: 700;
  }

  .panel-row {
    display: flex;
    justify-content: space-between;
    color: var(--muted);
  }

  .progress {
    height: 10px;
    background: rgba(31, 41, 51, 0.1);
    border-radius: 999px;
  }

  .progress-bar {
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, var(--accent), var(--accent-2));
  }

  .timeline {
    display: grid;
    gap: 1rem;
  }

  .stage {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 1rem;
    align-items: center;
    background: rgba(255, 250, 241, 0.8);
    border-radius: 14px;
    padding: 0.75rem 1rem;
  }

  .stage-index {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: var(--accent-2);
    color: #fff;
    display: grid;
    place-items: center;
    font-weight: 700;
  }

  .stage-title {
    font-weight: 600;
  }

  .stage-sub {
    color: var(--muted);
    font-size: 0.85rem;
  }

  .empty {
    color: var(--muted);
    font-size: 0.9rem;
  }

  .event {
    padding: 0.75rem 0;
    border-bottom: 1px dashed rgba(31, 41, 51, 0.1);
  }

  .event-time {
    color: var(--muted);
    font-size: 0.75rem;
  }

  @media (max-width: 820px) {
    .header {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style>
